import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Row, Col, Breadcrumb, Descriptions, Tag, Typography, Button,
    Space, Spin, message, Tooltip, Collapse, Timeline, Empty
} from "antd";
import {
    HomeOutlined, ArrowLeftOutlined, ExperimentOutlined,
    PrinterOutlined, FileExcelOutlined, LinkOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/axiosInstance";
import ReportPreviewModal from "../../../components/common/ReportPreviewModal";
import { buildReportPDF } from "../../../utils/reportPdfBuilder";
import { exportRowsToExcel, fmtMoney } from "../../../utils/reportExport";
import { actionMeta, actionDotColor, formatLogDateTime, timeAgo } from "../../../utils/productionLogMeta";
import { isPermissionAllowed } from "../../../utils/permissionHelper";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const PRODUCTION_STATUS_COLORS = {
    "Pending": "orange",
    "Ongoing": "blue",
    "Finished": "green",
};

const formatKg = (value) => fmtMoney(value);

const formatQty = (value) => Number(value || 0).toLocaleString();

const productLabel = (batch) => {
    if (!batch) return "-";
    if (batch.sp_code && batch.sp_name) return `${batch.sp_code} - ${batch.sp_name}`;
    return batch.sp_name || batch.sp_code || "-";
};

const percent = (numerator, denominator) => {
    const den = parseFloat(denominator || 0);
    const num = parseFloat(numerator || 0);
    if (den <= 0) return "0%";
    return `${Math.round((num / den) * 100)}%`;
};

const ProductionBatchDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    // Print / Excel export state
    const [printVisible, setPrintVisible] = useState(false);
    const [pdfDataUri, setPdfDataUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [exporting, setExporting] = useState(false);

    const fetchDetails = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/production/detail.php?id=${id}`);
            if (res.data.status === "success") setBatch(res.data);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to load production batch details.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    // Activity history of this batch (create / update / start / finish /
    // production input / approval / quality control entries).
    const fetchLogs = useCallback(async () => {
        if (!isPermissionAllowed('prod_logs_view')) {
            setLogs([]);
            return;
        }
        setLogsLoading(true);
        try {
            const res = await api.post("/production/log_list.php", {
                prodb_id: Number(id),
                page: 1,
                limit: 100,
            });
            setLogs(res.data.status === "success" ? (res.data.dataset || []) : []);
        } catch {
            setLogs([]);
        } finally {
            setLogsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // ── Derived figures ───────────────────────────────────────────────────
    const targetKg = parseFloat(batch?.target_kg || 0);
    const actualKg = parseFloat(batch?.actual_kg || 0);
    const qcApprovedKg = parseFloat(batch?.qc_approved_kg || 0);
    const qcRejectedKg = parseFloat(batch?.qc_rejecteded_kg || 0);

    const targetAchievement = percent(actualKg, targetKg);
    const qcPassRate = percent(qcApprovedKg, actualKg);
    const qcRejectRate = percent(qcRejectedKg, actualKg);

    // ── Print & Excel export ──────────────────────────────────────────────

    const buildFieldRows = () => [
        ["Batch Ref", batch.prodb_ref || "-"],
        ["Sales Product", productLabel(batch)],
        ["Status", batch.batch_status || "-"],
        ["Production Start Date", batch.prod_start_date ? dayjs(batch.prod_start_date).format("YYYY-MM-DD") : "-"],
        ["Production End Date", batch.prod_end_date ? dayjs(batch.prod_end_date).format("YYYY-MM-DD") : "-"],
        ["Expire Date", batch.expire_date ? dayjs(batch.expire_date).format("YYYY-MM-DD") : "-"],
        ["Target Quantity", formatQty(batch.target_qty)],
        ["Target Weight (Kg)", formatKg(batch.target_kg)],
        ["Actual Quantity", formatQty(batch.actual_qty)],
        ["Actual Weight (Kg)", formatKg(batch.actual_kg)],
        ["Processed By", batch.processed_by_name || "-"],
        ["QC Approved Quantity", formatQty(batch.qc_approved_qty)],
        ["QC Approved Weight (Kg)", formatKg(batch.qc_approved_kg)],
        ["QC Rejected Quantity", formatQty(batch.qc_rejecteded_qty)],
        ["QC Rejected Weight (Kg)", formatKg(batch.qc_rejecteded_kg)],
        ["Target Achievement (Kg)", targetAchievement],
        ["QC Pass Rate (Kg)", qcPassRate],
        ["QC Reject Rate (Kg)", qcRejectRate],
        ["Created By", batch.creator_name || "-"],
        ["Created Date", batch.cr_dt ? dayjs(batch.cr_dt).format("YYYY-MM-DD HH:mm") : "-"],
        ["Last Updated By", batch.updater_name || "-"],
        ["Last Updated Date", batch.lc_dt ? dayjs(batch.lc_dt).format("YYYY-MM-DD HH:mm") : "-"],
    ];

    const buildExportDoc = () => buildReportPDF({
        title: "PRODUCTION BATCH REPORT",
        subtitle: `Batch ${batch.prodb_ref}`,
        columns: [{ header: "Field" }, { header: "Value" }],
        rows: buildFieldRows(),
        summary: [
            { label: "Product", value: productLabel(batch) },
            { label: "Status", value: batch.batch_status || "-" },
            { label: "Target vs Actual (Kg)", value: `${formatKg(batch.target_kg)} / ${formatKg(batch.actual_kg)}` },
        ],
        orientation: "portrait",
    });

    const handlePrint = async () => {
        if (!batch) return;
        setPrinting(true);
        try {
            setPrintVisible(true);
            setPdfLoading(true);
            setPdfDataUri(null);
            const doc = await buildExportDoc();
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
            setPrinting(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const doc = await buildExportDoc();
            doc.save(`production-batch-${batch.prodb_ref}-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            message.success("PDF downloaded successfully.");
        } catch (err) {
            console.error("PDF download failed:", err);
            message.error("Failed to download PDF.");
        }
    };

    const handleExportExcel = async () => {
        if (!batch) return;
        setExporting(true);
        try {
            exportRowsToExcel({
                filename: `production-batch-${batch.prodb_ref}`,
                sheetName: "Production Batch",
                columns: [
                    { title: "Field", dataIndex: "field", width: 30 },
                    { title: "Value", dataIndex: "value", width: 40 },
                ],
                rows: buildFieldRows().map(([field, value]) => ({ field, value })),
            });
            message.success("Excel exported successfully.");
        } catch (err) {
            console.error("Excel export failed:", err);
            message.error("Failed to export Excel.");
        } finally {
            setExporting(false);
        }
    };

    // ── Collapsible detail sections ───────────────────────────────────────
    const collapseItems = batch ? [
        {
            key: "info",
            label: (
                <Space>
                    <Typography.Text strong>Batch Information</Typography.Text>
                    <Tag color={PRODUCTION_STATUS_COLORS[batch.batch_status] || "default"} style={{ fontWeight: 600 }}>
                        {batch.batch_status || "-"}
                    </Tag>
                </Space>
            ),
            children: (
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Batch Ref" span={2}>
                        <Typography.Text strong>{batch.prodb_ref}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Sales Product" span={2}>{productLabel(batch)}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag color={PRODUCTION_STATUS_COLORS[batch.batch_status] || "default"} style={{ fontWeight: 600 }}>
                            {batch.batch_status || "-"}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Processed By">{batch.processed_by_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Production Start Date">
                        {batch.prod_start_date ? dayjs(batch.prod_start_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Production End Date">
                        {batch.prod_end_date ? dayjs(batch.prod_end_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Expire Date">
                        {batch.expire_date ? dayjs(batch.expire_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Batch Id">{batch.prodb_id}</Descriptions.Item>
                    <Descriptions.Item label="Target Quantity">{formatQty(batch.target_qty)}</Descriptions.Item>
                    <Descriptions.Item label="Target Weight (Kg)">{formatKg(batch.target_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Created By">{batch.creator_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Created Date">
                        {batch.cr_dt ? dayjs(batch.cr_dt).format("YYYY-MM-DD HH:mm") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Updated By">{batch.updater_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Last Updated Date">
                        {batch.lc_dt ? dayjs(batch.lc_dt).format("YYYY-MM-DD HH:mm") : "-"}
                    </Descriptions.Item>
                </Descriptions>
            ),
        },
        ...(isPermissionAllowed('prod_logs_view') ? [{
            key: "history",
            label: (
                <Space>
                    <Typography.Text strong>Activity History</Typography.Text>
                    <Tag>{logs.length} log{logs.length === 1 ? "" : "s"}</Tag>
                </Space>
            ),
            children: (
                <Spin spinning={logsLoading}>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                        <Button
                            size="small"
                            icon={<LinkOutlined />}
                            onClick={() => navigate(`/production/production-logs?prodb=${batch.prodb_id}`)}
                        >
                            Open in Production Logs
                        </Button>
                    </div>
                    {logs.length === 0 && !logsLoading ? (
                        <Empty description="No activity recorded for this batch yet" />
                    ) : (
                        <Timeline
                            items={logs.map((entry) => {
                                const meta = actionMeta(entry.log_action);
                                return {
                                    dot: React.cloneElement(meta.icon, { style: { color: actionDotColor(entry.log_action) } }),
                                    children: (
                                        <div style={{ paddingBottom: 4 }}>
                                            <Space wrap size={8}>
                                                <Tag color={meta.color} icon={meta.icon} style={{ fontWeight: 600 }}>
                                                    {meta.label}
                                                </Tag>
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    {formatLogDateTime(entry.ch_dt)}{" \u00b7 "}{timeAgo(entry.ch_dt)}
                                                </Typography.Text>
                                            </Space>
                                            <div style={{ marginTop: 4 }}>
                                                <Typography.Text>{entry.log_message || "-"}</Typography.Text>
                                            </div>
                                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                by <strong>{entry.changed_by_name || "-"}</strong>
                                            </Typography.Text>
                                        </div>
                                    ),
                                };
                            })}
                        />
                    )}
                </Spin>
            ),
        }] : []),
        {
            key: "outcome",
            label: <Typography.Text strong>Production Outcome &amp; Quality Control</Typography.Text>,
            children: (
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Actual Quantity">{formatQty(batch.actual_qty)}</Descriptions.Item>
                    <Descriptions.Item label="Actual Weight (Kg)">{formatKg(batch.actual_kg)}</Descriptions.Item>
                    <Descriptions.Item label="QC Approved Quantity">{formatQty(batch.qc_approved_qty)}</Descriptions.Item>
                    <Descriptions.Item label="QC Approved Weight (Kg)">{formatKg(batch.qc_approved_kg)}</Descriptions.Item>
                    <Descriptions.Item label="QC Rejected Quantity">{formatQty(batch.qc_rejecteded_qty)}</Descriptions.Item>
                    <Descriptions.Item label="QC Rejected Weight (Kg)">{formatKg(batch.qc_rejecteded_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Target Achievement (Kg)">
                        <Typography.Text strong>{targetAchievement}</Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                            {formatKg(batch.actual_kg)} of {formatKg(batch.target_kg)} kg
                        </Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="QC Pass Rate (Kg)">
                        <Typography.Text strong>{qcPassRate}</Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                            {qcRejectRate} rejected
                        </Typography.Text>
                    </Descriptions.Item>
                </Descriptions>
            ),
        },
    ] : [];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Production' },
                                { title: 'Production Batches' },
                                { title: batch?.prodb_ref || 'Details' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ExperimentOutlined /> Production Batch Details
                        </h1>
                        <p className="fluid-page-subtitle">
                            {batch ? `Complete details for production batch ${batch.prodb_ref}` : "Loading production batch details..."}
                        </p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/production/production-batches')}
                            className="fluid-btn-secondary"
                        >
                            Back to Batches
                        </Button>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <Spin spinning={loading}>
                    {batch && (
                        <div className="fluid-card">
                            {isPermissionAllowed('prod_batches_export') && (
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "0 0 12px" }}>
                                    <Tooltip title="Print / export this batch as a PDF">
                                        <Button icon={<PrinterOutlined />} onClick={handlePrint} loading={printing}>
                                            Print
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Export this batch to Excel">
                                        <Button
                                            icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                                            onClick={handleExportExcel}
                                            loading={exporting}
                                        >
                                            Excel
                                        </Button>
                                    </Tooltip>
                                </div>
                            )}
                            <Collapse
                                items={collapseItems}
                                defaultActiveKey={["info", "outcome", "history"]}
                                expandIconPosition="end"
                            />
                        </div>
                    )}
                </Spin>
            </Content>

            <Footerdiv />

            <ReportPreviewModal
                visible={printVisible}
                onCancel={() => setPrintVisible(false)}
                title="Production Batch Report"
                pdfDataUri={pdfDataUri}
                loading={pdfLoading}
                showFiltersOption={false}
                onDownload={handleDownloadPDF}
            />
        </Layout>
    );
};

export default ProductionBatchDetail;
